'use client';

import { useCallback, useEffect, useState } from 'react';
import type { CartUI, EstimatedTreatsPoints } from '@libs/common-node';
import { type Nullable } from '@libs/core';
import { COMPONENTS } from '../../constants';
import { getEstimatedTreatsPointsForCart, logConsoleError } from '../../utils';
import type { IdentityUser } from '../UserSessionProvider';
const useTreatsInCart = (cart: Nullable<CartUI>, user: IdentityUser) => {
  const [estimatedTreatsPoints, setEstimatedTreatsPoints] = useState<EstimatedTreatsPoints | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastCart, setLastCart] = useState<Nullable<CartUI>>(null);
  const getEstimatedTreatsPoints = useCallback(async () => {
    if (!cart?.id) {
      return;
    }
    setIsLoading(true);
    try {
      const result = await getEstimatedTreatsPointsForCart({
        cartId: cart?.id,
        loyaltyId: user?.loyaltyid
      });
      setEstimatedTreatsPoints(result || null);
    } catch (error) {
      logConsoleError(JSON.stringify(error), {
        component: COMPONENTS.CART_PROVIDER,
        severity: 'MEDIUM',
        additionalInfo: 'Failed to get estimated treats points'
      });
    } finally {
      setIsLoading(false);
    }
  }, [cart, user]);
  const treatsPointsResponse = async () => await getEstimatedTreatsPoints();
  useEffect(() => {
    // do not call loyalty API if cart is null or cart total amount has not changed
    if (cart && cart.summary?.totalAmount !== lastCart?.summary?.totalAmount) {
      treatsPointsResponse();
      setLastCart(cart);
    }
  }, [cart, treatsPointsResponse, lastCart]);
  return {
    estimatedTreatsPoints,
    getEstimatedTreatsPoints,
    isGetEstimatedTreatsPointsLoading: isLoading
  };
};
export default useTreatsInCart;